import React, { useState, useEffect, useMemo } from "react";
import { FiShare2,FiEdit2 } from "react-icons/fi";
import { BiSearch } from "react-icons/bi";
import { getMeetingSessionById, updateMeetingSessionName } from "../../services/meetingServices";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { updateCustomMeetingName } from "../../services/meetingServices";
const AINotetaker = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [meetingData, setMeetingData] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      const response = await getMeetingSessionById(sessionId);
      console.log(response);
      setMeetingData(response.data);
    };
    fetchSession();
  }, [sessionId]);

  // Parse structured notes from meetingSummary JSON or individual fields
  const structuredNotes = useMemo(() => {
    if (!meetingData) return null;
    // Try meetingSummary first (full structured notes JSON)
    if (meetingData.meetingSummary) {
      try {
        return JSON.parse(meetingData.meetingSummary);
      } catch { /* fall through */ }
    }
    // Try building from individual JSON fields
    const notes = {};
    try { if (meetingData.assignments) notes.assignments = JSON.parse(meetingData.assignments); } catch {}
    try { if (meetingData.yourQuestions) notes.yourQuestions = JSON.parse(meetingData.yourQuestions); } catch {}
    try { if (meetingData.theirQuestions) notes.theirQuestions = JSON.parse(meetingData.theirQuestions); } catch {}
    if (Object.keys(notes).length > 0) return notes;
    return null;
  }, [meetingData]);

  const handleEditName = () => {
    const currentName =
      meetingData?.custom_session_name ||
      meetingData?.sessionId ||
      meetingData?.meetingName ||
      "";
    setEditedName(currentName);
    setIsEditingName(true);
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName("");
  };

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      toast.error("Meeting name cannot be empty");
      return;
    }

    setIsSavingName(true);
    try {
      await updateMeetingSessionName({
        sessionId: sessionId,
        custom_session_name: editedName.trim(),
      });

      // Update local state
      setMeetingData((prev) => ({
        ...prev,
        custom_session_name: editedName.trim(),
      }));

      setIsEditingName(false);
      toast.success("Meeting name updated successfully");
    } catch (error) {
      console.error("Error updating meeting name:", error);
      toast.error("Failed to update meeting name. Please try again.");
    } finally {
      setIsSavingName(false);
    }
  };

  // Format date to "Oct 27, 2025" format
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${
      months[date.getMonth()]
    } ${date.getDate()}, ${date.getFullYear()}`;
  };

  // Format duration to "1h 12m" format
  const formatDuration = (durationString) => {
    if (!durationString) return "-";
    // If duration is already in format like "1h 12m", return as is
    if (typeof durationString === "string" && durationString.includes("h")) {
      return durationString;
    }
    // If duration is in minutes, convert to hours and minutes
    if (typeof durationString === "number") {
      const hours = Math.floor(durationString / 60);
      const minutes = durationString % 60;
      return `${hours}h ${minutes}m`;
    }
    // If duration is in milliseconds or seconds, convert
    if (typeof durationString === "number" && durationString > 3600) {
      const totalMinutes = Math.floor(durationString / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    }
    return durationString;
  };

  // Format participants to "X attendees (Y on stage)" format
  const formatParticipants = (participants) => {
    if (!participants) return "-";
    const total = participants.length;
    // For now, we'll assume 4 on stage if not provided
    const onStage = 4; // This could come from the API if available
    // return `${total} attendees (${onStage} on stage)`;
    return `${total} attendees`;
  };
  // const meetingData = {
  //   title: "Marketing Session #3",
  //   date: "Oct 27, 2025",
  //   duration: "1h 12m",
  //   attendees: "Participants: 38 attendees (4 on stage)",
  //   recording: "[Watch Replay]",
  //   language: "English + Spanish",
  // };

  return (
    <div className="min-h-screen flex items-start justify-center p-2 sm:p-3 md:p-6 overflow-x-hidden">
      <div className="w-full max-w-5xl overflow-x-hidden">
        <div className="rounded-xl p-3 sm:p-4 md:p-6 lg:p-8 primary-card-background overflow-x-hidden">
          {/* Top metadata bar */}
          <div className="bg-transparent text-white mb-4 md:mb-6">
            {/* Centered Title */}
            <div className="flex justify-center mb-3 sm:mb-4 md:mb-6">
              <h1 className="text-white font-poppins text-sm sm:text-base md:text-lg lg:text-2xl tracking-widest">
                AI TRANSCRIPT
              </h1>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-2 gap-x-2 sm:gap-x-4 md:gap-x-24 lg:gap-x-40 xl:gap-x-48 gap-y-2 sm:gap-y-3 md:gap-y-4 text-xs sm:text-sm md:text-base">
              {/* Left Column */}
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <div className="flex flex-col gap-0.5 sm:gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-300 text-xs sm:text-sm md:text-base font-bold whitespace-nowrap">
                      Meeting Name:
                    </span>
                    {!isEditingName && meetingData && (
                      <button
                        onClick={handleEditName}
                        className="text-white bg-primaryPurple p-1 rounded hover:text-white transition-colors"
                        title="Edit meeting name"
                      >
                        <FiEdit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    )}
                  </div>
                  {isEditingName ? (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        className="bg-gray-800 text-white font-bold text-xs sm:text-sm md:text-base px-2 py-1 rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 min-w-0"
                        placeholder="Enter meeting name"
                        disabled={isSavingName}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSaveName();
                          } else if (e.key === "Escape") {
                            handleCancelEdit();
                          }
                        }}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveName}
                          disabled={isSavingName}
                          className="px-2 py-1 bg-primaryPurple hover:bg-primaryPurple/80 text-white text-xs rounded transition disabled:bg-primaryPurple/40 disabled:cursor-not-allowed"
                        >
                          {isSavingName ? "Saving..." : "Save"}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={isSavingName}
                          className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded transition disabled:bg-gray-500 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <span className="text-white font-bold text-xs sm:text-sm md:text-base break-all sm:break-words md:truncate min-w-0">
                      {meetingData
                        ? meetingData.custom_session_name ||
                          meetingData.sessionId ||
                          meetingData.meetingName ||
                          "-"
                        : "-"}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-gray-300 text-xs sm:text-sm md:text-base font-bold whitespace-nowrap">
                    Date:
                  </span>
                  <span className="text-white font-bold text-xs sm:text-sm md:text-base">
                    {meetingData
                      ? formatDate(
                          meetingData.startDate ||
                            meetingData.scheduledStartTime ||
                            meetingData.date
                        )
                      : "-"}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-gray-300 text-xs sm:text-sm md:text-base font-bold whitespace-nowrap">
                    Duration:
                  </span>
                  <span className="text-white font-bold text-xs sm:text-sm md:text-base">
                    {meetingData ? formatDuration(meetingData.duration) : "-"}
                  </span>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <div className="flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-gray-300 text-xs sm:text-sm md:text-base font-bold whitespace-nowrap">
                    Agora Channel:
                  </span>
                  <span className="text-xs font-mono text-purple-300 bg-purple-500/10 px-2 py-0.5 rounded inline-block">
                    {meetingData
                      ? meetingData.agoraChannel || meetingData.roomId || "-"
                      : "-"}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-gray-300 text-xs sm:text-sm md:text-base font-bold whitespace-nowrap">
                    Session ID:
                  </span>
                  <span className="text-white font-mono text-xs sm:text-sm md:text-base break-all">
                    {meetingData
                      ? meetingData.sessionId || "-"
                      : "-"}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-gray-300 text-xs sm:text-sm md:text-base font-bold whitespace-nowrap">
                    Participants:
                  </span>
                  <span className="text-white font-bold text-xs sm:text-sm md:text-base">
                    {meetingData
                      ? formatParticipants(meetingData.participants)
                      : "-"}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-gray-300 text-xs sm:text-sm md:text-base font-bold whitespace-nowrap">
                    Recording:
                  </span>
                  <span className="text-white font-bold text-xs sm:text-sm md:text-base">
                    {meetingData?.sessionRecordings?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {meetingData.sessionRecordings.map((rec, idx) => (
                          <button
                            key={rec.id}
                            onClick={() => navigate(`/dashboard/recordings/${rec.id}`)}
                            className="text-purple-300 hover:text-white hover:underline text-xs sm:text-sm md:text-base"
                          >
                            {meetingData.sessionRecordings.length > 1
                              ? `[Part ${idx + 1}]`
                              : "[Watch Replay]"}
                          </button>
                        ))}
                      </div>
                    ) : meetingData &&
                      (meetingData.recordingUrl || meetingData.recording) ? (
                      <button
                        onClick={() => {
                          if (meetingData.recordingId) {
                            navigate(`/dashboard/recordings/${meetingData.recordingId}`);
                          } else {
                            const url = meetingData.recordingUrl || meetingData.recording;
                            if (url) window.open(url, "_blank");
                          }
                        }}
                        className="text-purple-300 hover:text-white hover:underline text-xs sm:text-sm md:text-base break-words sm:break-normal"
                      >
                        [Watch Replay]
                      </button>
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-gray-300 text-xs sm:text-sm md:text-base font-bold whitespace-nowrap">
                    Language detected:
                  </span>
                  <span className="text-white font-bold text-xs sm:text-sm md:text-base">
                    {meetingData
                      ? meetingData.language || meetingData.languages || "-"
                      : "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Top Tabs */}
          <div className="w-full mb-3 sm:mb-4 md:mb-6">
            <div className="flex flex-wrap gap-2 md:gap-3 md:flex-nowrap">
              {[
                { key: "summary", label: "Summary" },
                { key: "transcript", label: "Full Transcript" },
                { key: "assignments", label: "Meeting Assignments" },
                { key: "yourQuestions", label: "Your Questions" },
                { key: "theirQuestions", label: "Their Questions" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-xs md:text-sm font-poppins tracking-wide transition whitespace-nowrap ${
                    activeTab === tab.key
                      ? "bg-[#7700FF] text-white"
                      : "bg-gray-800 text-gray-200 hover:bg-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search row */}
          {/* <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder='Type a name or word (e.g. "John", "budget", "timeline")'
                className="w-full bg-white/90 text-gray-700 placeholder-gray-500 font-montserrat text-sm px-4 py-3 rounded-lg focus:outline-none"
              />
              <BiSearch className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
            </div>
            <button className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-poppins">
              Search
            </button>
          </div> */}

          {/* Content area */}
          <div className="overflow-x-hidden">
            {/* ── Summary Tab ── */}
            {activeTab === "summary" && (
              <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 overflow-x-hidden">
                <h3 className="text-black font-montserrat text-sm sm:text-base md:text-lg font-bold mb-2 sm:mb-3 md:mb-4 break-words">
                  Summary
                </h3>

                {structuredNotes?.summary ? (
                  <div className="space-y-4 text-xs sm:text-xs md:text-sm text-gray-800 overflow-x-hidden">
                    {/* Meeting Purpose */}
                    {structuredNotes.summary.purpose && (
                      <div>
                        <h4 className="text-black font-semibold text-xs sm:text-sm md:text-base mb-1">Meeting Purpose</h4>
                        <p className="break-words">{structuredNotes.summary.purpose}</p>
                      </div>
                    )}

                    {/* Key Takeaways */}
                    {structuredNotes.summary.keyTakeaways?.length > 0 && (
                      <div>
                        <h4 className="text-black font-semibold text-xs sm:text-sm md:text-base mb-1">Key Takeaways</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {structuredNotes.summary.keyTakeaways.map((item, idx) => (
                            <li key={idx} className="break-words">{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Topics */}
                    {structuredNotes.summary.topics?.length > 0 && (
                      <div>
                        <h4 className="text-black font-semibold text-xs sm:text-sm md:text-base mb-1">Topics Discussed</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {structuredNotes.summary.topics.map((item, idx) => (
                            <li key={idx} className="break-words">{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Next Steps */}
                    {structuredNotes.summary.nextSteps?.length > 0 && (
                      <div>
                        <h4 className="text-black font-semibold text-xs sm:text-sm md:text-base mb-1">Next Steps</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {structuredNotes.summary.nextSteps.map((item, idx) => (
                            <li key={idx} className="break-words">{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : meetingData?.aisummary ? (
                  <div className="prose prose-sm max-w-none break-words overflow-x-hidden text-xs sm:text-xs md:text-sm text-gray-800">
                    {meetingData.aisummary.split(/\r?\n/).map((line, idx) =>
                      line.trim() === "" ? (
                        <br key={idx} />
                      ) : (
                        <span key={idx} className="break-words">{line}<br /></span>
                      )
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-xs sm:text-xs md:text-sm">No summary available yet.</p>
                )}
              </div>
            )}

            {/* ── Full Transcript Tab ── */}
            {activeTab === "transcript" && (
              <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 overflow-x-hidden">
                <h3 className="text-black font-montserrat text-sm sm:text-base md:text-lg font-bold mb-2 sm:mb-3 md:mb-4 break-words">
                  Full Transcript
                </h3>

                {structuredNotes?.fullTranscript?.length > 0 ? (
                  <div className="space-y-3 text-xs sm:text-xs md:text-sm text-gray-800 overflow-x-hidden">
                    {structuredNotes.fullTranscript.map((entry, idx) => (
                      <div key={idx} className="flex gap-2 sm:gap-3">
                        <div className="flex-shrink-0">
                          <span className="inline-block bg-[#7700FF] text-white text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium">
                            {entry.speaker}
                          </span>
                          {entry.timestamp && (
                            <span className="block text-gray-400 text-[10px] mt-0.5">
                              {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                          )}
                        </div>
                        <p className="break-words flex-1 min-w-0">{entry.text}</p>
                      </div>
                    ))}
                  </div>
                ) : meetingData?.fulltranscript ? (
                  <p className="text-gray-800 text-xs sm:text-xs md:text-sm break-words overflow-x-hidden">
                    {meetingData.fulltranscript.split(/\r?\n/).map((line, idx) =>
                      line.trim() === "" ? (
                        <br key={idx} />
                      ) : (
                        <span key={idx} className="break-words">{line}<br /></span>
                      )
                    )}
                  </p>
                ) : (
                  <p className="text-gray-500 text-xs sm:text-xs md:text-sm">No transcript available yet.</p>
                )}
              </div>
            )}

            {/* ── Meeting Assignments Tab ── */}
            {activeTab === "assignments" && (
              <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 overflow-x-hidden">
                <h3 className="text-black font-montserrat text-sm sm:text-base md:text-lg font-bold mb-2 sm:mb-3 md:mb-4 break-words">
                  Meeting Assignments
                </h3>

                {structuredNotes?.assignments?.length > 0 ? (
                  <div className="space-y-3 text-xs sm:text-xs md:text-sm text-gray-800 overflow-x-hidden">
                    {structuredNotes.assignments.map((item, idx) => (
                      <div key={idx} className="border-l-4 border-[#7700FF] pl-3 py-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="bg-gray-200 text-gray-700 text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium">
                            {item.assignee}
                          </span>
                        </div>
                        <p className="font-medium text-black break-words">{item.task}</p>
                        {item.context && (
                          <p className="text-gray-500 italic mt-1 break-words text-[11px] sm:text-xs">"{item.context}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-xs sm:text-xs md:text-sm">No meeting assignments found.</p>
                )}
              </div>
            )}

            {/* ── Your Questions Tab ── */}
            {activeTab === "yourQuestions" && (
              <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 overflow-x-hidden">
                <h3 className="text-black font-montserrat text-sm sm:text-base md:text-lg font-bold mb-2 sm:mb-3 md:mb-4 break-words">
                  Your Questions
                </h3>
                <p className="text-gray-400 text-[10px] sm:text-xs mb-3">Questions asked by the host</p>

                {structuredNotes?.yourQuestions?.length > 0 ? (
                  <div className="space-y-3 text-xs sm:text-xs md:text-sm text-gray-800 overflow-x-hidden">
                    {structuredNotes.yourQuestions.map((item, idx) => (
                      <div key={idx} className="border-l-4 border-[#00c853] pl-3 py-1">
                        <p className="font-medium text-black break-words">Q: {item.question}</p>
                        {item.answer ? (
                          <p className="text-gray-600 mt-1 break-words">A: {item.answer}</p>
                        ) : (
                          <p className="text-gray-400 italic mt-1 text-[11px] sm:text-xs">Unanswered</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-xs sm:text-xs md:text-sm">No host questions found.</p>
                )}
              </div>
            )}

            {/* ── Their Questions Tab ── */}
            {activeTab === "theirQuestions" && (
              <div className="relative overflow-x-hidden">
                <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 shadow-lg overflow-x-hidden">
                  <h3 className="text-black font-montserrat text-sm sm:text-base md:text-lg font-bold mb-2 sm:mb-3 md:mb-4 break-words">
                    Their Questions
                  </h3>
                  <p className="text-gray-400 text-[10px] sm:text-xs mb-3">Questions asked by other participants</p>

                  {structuredNotes?.theirQuestions?.length > 0 ? (
                    <div className="space-y-3 text-xs sm:text-xs md:text-sm text-gray-800 overflow-x-hidden">
                      {structuredNotes.theirQuestions.map((item, idx) => (
                        <div key={idx} className="border-l-4 border-orange-400 pl-3 py-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="bg-gray-200 text-gray-700 text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium">
                              {item.askedBy}
                            </span>
                          </div>
                          <p className="font-medium text-black break-words">Q: {item.question}</p>
                          {item.answer ? (
                            <p className="text-gray-600 mt-1 break-words">A: {item.answer}</p>
                          ) : (
                            <p className="text-gray-400 italic mt-1 text-[11px] sm:text-xs">Unanswered</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-xs sm:text-xs md:text-sm">No participant questions found.</p>
                  )}
                </div>
                <div className="mt-2 sm:mt-2 md:mt-3 text-xs text-gray-300 break-words">
                  AI-generated insights. Please verify key data and assignments
                  before distribution.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AINotetaker;
